import React, { useState } from 'react';
import getConfig from 'next/config';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { sendHttpRequest } from '../../utils/sendHttpRequest';
import styled from '../../theme/styled-with-theme';

import { Button } from '../Button';
import Link from '../Link';
import { StateSelector } from './StateSelector';
import { FormInput } from './FormInput';

const { publicRuntimeConfig } = getConfig();
const { stargateway } = publicRuntimeConfig;

export const DataRequestSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, 'Please enter a name longer than 1 letter')
    .max(256, 'First Name is too long')
    .required('Required'),
  lastName: Yup.string()
    .min(2, 'Please enter a name longer than 1 letter')
    .max(256, 'Last Name is too long')
    .required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  country: Yup.string().required('Required'),
  state: Yup.string().min(2, 'Required').required('Required')
});

interface FormValues extends Record<string, unknown> {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  state: string;
}

export const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  country: 'United States',
  state: ''
};

const Centered = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Error = styled(Centered)`
  color: red;
`;

const Row = styled(Centered)`
  padding: ${({ theme }) => theme.spacing.sm};
`;

const HelpText = styled(Centered)`
  color: ${({ theme }) => theme.color.inputLabelText};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SuccessMessage = styled('div')`
  text-align: center;
  color: ${({ theme }) => theme.color.inputLabelText};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
`;

export function RequestForm() {
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (data: FormValues) => {
    if (error) {
      setError(false);
    }
    if (success) {
      setSuccess(false);
    }
    const STARGATEWAY_URL = `${stargateway}/open/v2`;
    const response = await sendHttpRequest<true>({
      method: 'post',
      url: `${STARGATEWAY_URL}/data-privacy`,
      data,
      validateResponse: () => true,
      retries: 2,
      backoffMs: 500
    });

    if (response.status !== 200) {
      setError(true);
    } else {
      setSuccess(true);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={DataRequestSchema}
      onSubmit={submit}
    >
      <Form>
        <Row>
          <FormInput name="email" label="Email address" type="email" />
        </Row>
        <Error>
          <ErrorMessage name="email" />
        </Error>
        <HelpText>Enter email associated with your Greenlight account</HelpText>
        <Row>
          <FormInput name="firstName" label="First name" type="text" />
        </Row>
        <Error>
          <ErrorMessage name="firstName" />
        </Error>
        <Row>
          <FormInput name="lastName" label="Last name" type="text" />
        </Row>
        <Error>
          <ErrorMessage name="lastName" />
        </Error>
        <Row>
          <StateSelector />
        </Row>
        <Error>
          <ErrorMessage name="state" />
        </Error>
        <Row>
          <Button data-testid="submit-button" type="submit">
            Submit
          </Button>
        </Row>
        {error && (
          <Error data-testid="error-message">
            We encountered an error submitting your information. Please try
            again later or submit a request to this email:{' '}
            <Link href="mailto:CAPrivacy@greenlight.com">
              CAPrivacy@greenlight.com
            </Link>
          </Error>
        )}
        {success && (
          <SuccessMessage data-testid="success-message">
            Thank you for your submission. We will reach out to you within 15
            business days once we process your request. If you have any
            questions, please refer to our{' '}
            <Link href="/privacy#notice-to-california-residents">
              Privacy Policy
            </Link>{' '}
            for more information.
          </SuccessMessage>
        )}
      </Form>
    </Formik>
  );
}
